import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Pill, Pencil, Trash2, Zap, CheckCircle, SkipForward, Clock, Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { getTimeline, groupByDay, formatLocalShort } from '@/lib/history';
import { resolveDeviceTimezone } from '@/lib/doses';
import { SeverityColors } from '@/lib/theme';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'meds', label: 'Meds' },
  { key: 'interactions', label: 'Interactions' },
  { key: 'doses', label: 'Doses' },
];

function matchesFilter(type, f) {
  if (f === 'all') return true;
  if (f === 'meds') return type.startsWith('medication.');
  if (f === 'interactions') return type === 'interaction.run';
  if (f === 'doses') return type.startsWith('dose.');
  return true;
}

function visualFor(type) {
  switch (type) {
    case 'medication.added': return { Icon: Pill, color: '#5B2D1F', bg: '#EEF4F7' };
    case 'medication.updated': return { Icon: Pencil, color: '#5B2D1F', bg: '#EEF4F7' };
    case 'medication.removed': return { Icon: Trash2, color: '#B0303A', bg: '#FCE7EA' };
    case 'interaction.run': return { Icon: Zap, color: '#B07A1F', bg: '#FFF4DF' };
    case 'dose.taken': return { Icon: CheckCircle, color: '#1F7A4A', bg: '#E4F4EB' };
    case 'dose.skipped': return { Icon: SkipForward, color: '#5C6B72', bg: '#E8ECEE' };
    default: return { Icon: Clock, color: '#4B6B79', bg: '#EEF4F7' };
  }
}

export default function HistoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tz = user?.timezone || resolveDeviceTimezone();

  const [events, setEvents] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async (windowDays) => {
    setError(null);
    try {
      const res = await getTimeline(windowDays, tz);
      setEvents(res.events);
    } catch (e) {
      setError(e.message || 'Could not load history.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [tz]);

  useEffect(() => { load(days); }, [days, load]);

  const filtered = useMemo(() => events.filter((e) => matchesFilter(e.type, filter)), [events, filter]);
  const groups = useMemo(() => groupByDay(filtered, tz), [filtered, tz]);

  const onLoadMore = () => {
    if (loadingMore || days >= 365) return;
    setLoadingMore(true);
    setDays((d) => Math.min(d + 30, 365));
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-12" data-testid="history-screen">
      <div className="flex items-center gap-2 mb-1">
        <button onClick={() => navigate(-1)} data-testid="history-back-btn" className="p-1 rounded-lg hover:bg-black/5">
          <ChevronLeft className="h-6 w-6" style={{ color: '#5B2D1F' }} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-extrabold" style={{ color: '#5B2D1F' }} data-testid="history-screen-title">Activity history</h1>
          <p className="text-xs mt-0.5" style={{ color: '#4B6B79' }}>Last {days} days · {filtered.length} events</p>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mt-4 mb-4" data-testid="history-filters">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`history-filter-${f.key}`}
              className="px-4 py-2 rounded-full border text-sm font-bold transition-colors"
              style={active ? { backgroundColor: '#D77A52', borderColor: '#D77A52', color: '#FFFFFF' } : { backgroundColor: '#EEF4F7', borderColor: '#D7E2E8', color: '#5B2D1F' }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {error && (
        <div data-testid="history-error" className="p-4 rounded-xl border mb-4" style={{ backgroundColor: '#FCE7EA', borderColor: '#B0303A' }}>
          <p className="font-semibold" style={{ color: '#B0303A' }}>{error}</p>
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center py-16 gap-3">
          <img src="/brand/logo-round.png" alt="" className="w-14 h-14 rounded-full opacity-35" />
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#D77A52' }} />
          <p className="text-base" style={{ color: '#4B6B79' }}>Loading your history…</p>
        </div>
      )}

      {!loading && groups.length === 0 && (
        <div data-testid="history-empty" className="rounded-2xl border p-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
          <img src="/brand/logo-round.png" alt="" className="w-12 h-12 rounded-full opacity-35 mb-4" />
          <h2 className="text-xl font-extrabold mb-1" style={{ color: '#5B2D1F' }}>No activity yet</h2>
          <p className="text-base" style={{ color: '#4B6B79' }}>Add a medication or run an interaction check to see events here.</p>
        </div>
      )}

      {!loading && groups.map((g, gi) => (
        <div key={`${g.label}-${gi}`} className="mb-6">
          <p className="text-xs font-extrabold uppercase tracking-widest mb-2" style={{ color: '#4B6B79' }}>{g.label}</p>
          <div className="space-y-2">
            {g.events.map((e, ei) => {
              const v = visualFor(e.type);
              const sevTint = e.type === 'interaction.run' && e.severity ? SeverityColors[e.severity] : null;
              const interactive = e.type === 'interaction.run';
              return (
                <button
                  key={`${e.id}-${ei}`}
                  onClick={interactive ? () => navigate('/interactions') : undefined}
                  data-testid={`history-event-${gi}-${ei}`}
                  className={`w-full flex items-center gap-3 rounded-2xl border p-4 min-h-[44px] text-left transition-opacity ${interactive ? 'hover:opacity-90 cursor-pointer' : 'cursor-default'}`}
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderColor: '#D7E2E8',
                    ...(sevTint ? { borderLeftWidth: 4, borderLeftColor: sevTint.border } : {}),
                  }}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: v.bg }}>
                    <v.Icon className="h-[18px] w-[18px]" style={{ color: v.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-extrabold truncate" style={{ color: '#0F2A35' }}>{e.title}</p>
                    {e.subtitle && <p className="text-sm mt-0.5 line-clamp-2" style={{ color: '#4B6B79' }}>{e.subtitle}</p>}
                    <p className="text-xs mt-1" style={{ color: '#4B6B79' }}>{formatLocalShort(e.occurred_at, tz)}</p>
                  </div>
                  {interactive && <ChevronRight className="h-4 w-4 shrink-0" style={{ color: '#4B6B79' }} />}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {!loading && days < 365 && (
        <button
          onClick={onLoadMore}
          disabled={loadingMore}
          data-testid="history-load-more"
          className="w-full p-4 rounded-xl border text-base font-extrabold text-center transition-opacity hover:opacity-90 disabled:opacity-70"
          style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8', color: '#5B2D1F' }}
        >
          {loadingMore ? <Loader2 className="h-5 w-5 animate-spin mx-auto" style={{ color: '#D77A52' }} /> : 'Load 30 more days'}
        </button>
      )}
    </div>
  );
}
